import posthog, { type PostHogConfig } from "posthog-js"
import { PostHogProvider } from "posthog-js/react"
import { memo, useEffect } from "react"
import { useLocation } from "react-router"

import { APP_NAME, APP_VERSION, ENVIRONMENT } from "@/config"

const posthogEnvironmentProps = { environment: ENVIRONMENT }

const options: Partial<PostHogConfig> = {
  api_host: import.meta.env.VITE_POSTHOG_HOST,
  capture_pageview: false,
  debug: false,
  ui_host: import.meta.env.VITE_POSTHOG_UI_HOST,
  rageclick: true,
  defaults: "2026-01-30",
  logs: {
    serviceName: `${APP_NAME}-frontend`,
    environment: ENVIRONMENT,
    serviceVersion: APP_VERSION,
    captureConsoleLogs: true,
  },
}

// eslint-disable-next-line react-refresh/only-export-components
export const loadPostHog = () => {
  const apiKey = import.meta.env.VITE_POSTHOG_API_KEY

  if (apiKey) {
    posthog.init(apiKey, {
      ...options,
      loaded: (posthog) => {
        options.loaded?.(posthog)

        posthog.register(posthogEnvironmentProps)
        posthog.setPersonPropertiesForFlags(posthogEnvironmentProps)
      },
    })
  } else {
    posthog.init("fake-token", {
      autocapture: false,
      capture_pageview: false,
      disable_session_recording: true,
      loaded: (posthog) => {
        posthog.opt_out_capturing()
      },
    })
  }
}

function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation()

  useEffect(() => {
    posthog.capture("$pageview")
  }, [location.pathname])

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>
}

const MemoizedAnalyticsProvider = memo(AnalyticsProvider)
MemoizedAnalyticsProvider.displayName = "AnalyticsProvider"

export default MemoizedAnalyticsProvider

// eslint-disable-next-line react-refresh/only-export-components
export const sendSearchEvent = (query: string) => {
  posthog.capture("search", {
    query,
  })
}

type FilterChangedEvent = {
  filterGroup: string
  filterKey: string
  isActive: boolean
  filterLabel?: string
  value?: boolean | number | string | null
  metadata?: Record<string, boolean | number | string | null | undefined>
}

type FilterSectionToggleEvent = {
  section: string
  isExpanded: boolean
}

type FilterPanelToggleEvent = {
  panel: string
  isOpen: boolean
}

type LcaPdfDownloadedEvent = {
  clickTarget: "badge" | "label"
  modelNr?: string
  rating: string
  ratingBand: string
}

// eslint-disable-next-line react-refresh/only-export-components
export const sendLcaPdfDownloadedEvent = ({
  clickTarget,
  modelNr,
  rating,
  ratingBand,
}: LcaPdfDownloadedEvent) => {
  posthog.capture("lca_pdf_downloaded", {
    click_target: clickTarget,
    model_nr: modelNr,
    rating,
    rating_band: ratingBand,
  })
}

// eslint-disable-next-line react-refresh/only-export-components
export const sendFilterChangedEvent = ({
  filterGroup,
  filterKey,
  isActive,
  filterLabel,
  value,
  metadata,
}: FilterChangedEvent) => {
  posthog.capture("filter_changed", {
    filter_group: filterGroup,
    filter_key: filterKey,
    filter_label: filterLabel,
    is_active: isActive,
    value,
    ...metadata,
  })
}

// eslint-disable-next-line react-refresh/only-export-components
export const sendFilterSectionToggleEvent = ({ section, isExpanded }: FilterSectionToggleEvent) => {
  posthog.capture("filter_section_toggled", {
    section,
    is_expanded: isExpanded,
  })
}

// eslint-disable-next-line react-refresh/only-export-components
export const sendFilterPanelToggleEvent = ({ panel, isOpen }: FilterPanelToggleEvent) => {
  posthog.capture("filter_panel_toggled", {
    panel,
    is_open: isOpen,
  })
}
